import { findAllMajorsName, findMinorNames } from '@vivalavida-company/shared-utils'

/**
 * SEO 태그 생성 유틸
 *
 * 근거: SEO 스펙 v1 (노션 "웹) 모임·피드 페이지 SEO 태그 매핑")
 * 검색 이용자는 행정 표기(`서울특별시 종로구`)로 검색하지 않고 축약 표기(`서울`, `종로`)로
 * 검색한다. (실측: 서울놀거리 52,110 vs 수도권놀거리 180)
 */

const uniq = (values: string[]) => [...new Set(values.map((v) => v.trim()).filter(Boolean))]

/**
 * 축약형과 함께 원본 표기도 내보내는 시도.
 * `도` 표기 자체의 검색량이 살아있는 예외다. (실측: 경기도가볼만한곳 54,600) - 스펙 §2-2
 */
const MAJORS_KEEPING_FULL_NAME = new Set(['경기도', '강원도'])

/**
 * 시도 원본명 → { 축약명, 시군구 원본명 → 축약명 }
 *
 * 축약형을 직접 만들지 않고 shared-utils 의 지역 데이터를 그대로 쓴다.
 * 이 데이터는 `강남구`→`강남` 처럼 접미사를 떼면서도, 한 글자로 줄어드는 경우는
 * `북구`→`북구` 로 원본을 유지하고 있어 스펙 §2-2 의 표와 정확히 일치한다.
 *
 * 시군구는 시도별로 분리해 담는다. `중구`·`북구` 등은 여러 시도에 중복 존재하므로
 * 시도를 먼저 확정한 뒤 그 안에서 찾아야 한다.
 */
const regionIndex = (() => {
  const index = new Map<string, { short: string; minors: Map<string, string> }>()
  findAllMajorsName().forEach((major) => {
    const minors = new Map<string, string>()
    findMinorNames(major.regionCode).forEach((minor) => minors.set(minor.full, minor.short))
    index.set(major.full, { short: major.short, minors })
  })
  return index
})()

export type RegionParts = {
  /** 시도 축약명. `서울`, `경기`, `충북` */
  major: string
  /** 시도 원본명. `서울특별시`, `경기도` */
  majorFull: string
  /** 시군구 축약명. `강남`, `북구`. 시군구가 없으면 빈 문자열 */
  minor: string
}

/**
 * 행정 지역명을 시도/시군구로 분해한다.
 *
 * 구조화된 값이 필요한 곳(JSON-LD 의 `addressRegion`/`addressLocality` 등)에서 쓴다.
 * 키워드 나열이 필요하면 {@link splitRegion} 을 쓸 것. 그쪽은 `경기도` 같은 병기 표기가
 * 배열에 섞여 들어오므로 `[1]` 이 시군구라고 가정할 수 없다.
 *
 * @example
 * splitRegionParts('서울특별시 강남구') // { major: '서울', majorFull: '서울특별시', minor: '강남' }
 * splitRegionParts('경기도 수원시')     // { major: '경기', majorFull: '경기도',     minor: '수원' }
 * splitRegionParts('세종시')            // { major: '세종', majorFull: '세종시',     minor: '' }
 *
 * @returns 지역 데이터에 없는 시도가 들어오면 `null`. 호출부에서 기존 값을 유지하는 등
 *   폴백을 정할 수 있게 한다.
 */
export const splitRegionParts = (regionName: string | null | undefined): RegionParts | null => {
  if (!regionName) return null

  const [majorName, minorName] = regionName.trim().split(/\s+/).filter(Boolean)
  if (!majorName) return null

  const major = regionIndex.get(majorName)
  if (!major) return null

  return {
    major: major.short,
    majorFull: majorName,
    minor: minorName ? (major.minors.get(minorName) ?? minorName) : '',
  }
}

/**
 * 행정 지역명을 검색 가능한 축약 키워드로 분해한다. (스펙 §2-2)
 *
 * 합성하지 않고 병렬로 둔다. `광주골프조인동호회`(검색량 ≈ 0) 같은 합성어를 만들지 않고
 * `광주` + `골프조인동호회` 로 나눠두면 검색엔진이 조합 검색에 알아서 매칭한다. (스펙 §1-3)
 *
 * @example
 * splitRegion('서울특별시 강남구') // ['서울', '강남']
 * splitRegion('광주광역시 북구')   // ['광주', '북구']  (한 글자가 되므로 원본 유지)
 * splitRegion('경기도 수원시')     // ['경기', '경기도', '수원']
 * splitRegion('세종시')            // ['세종']
 *
 * @param regionName `regionCodeToName()` 이 만든 `'{시도} {시군구}'` 형태의 문자열.
 *   모임 상세의 `regionName`, 피드 상세의 `gatheringInfo.region` 둘 다 이 형태다.
 * @returns 중복 제거된 지역 키워드. 매칭되지 않는 토큰은 원본을 그대로 남긴다
 *   (지역 데이터가 갱신돼 매칭에 실패해도 키워드가 조용히 사라지지 않도록).
 */
export const splitRegion = (regionName: string | null | undefined): string[] => {
  if (!regionName) return []

  const [majorName, ...minorNames] = regionName.trim().split(/\s+/).filter(Boolean)
  if (!majorName) return []

  const major = regionIndex.get(majorName)
  if (!major) return uniq([majorName, ...minorNames])

  const keywords = [major.short]
  if (MAJORS_KEEPING_FULL_NAME.has(majorName)) keywords.push(majorName)
  minorNames.forEach((minorName) => keywords.push(major.minors.get(minorName) ?? minorName))

  return uniq(keywords)
}

// ────────────────────────────────────────────────────────────────────────────
// 카테고리 매핑 (스펙 §3)
// ────────────────────────────────────────────────────────────────────────────

export type CategoryKeywords = {
  /**
   * title 에 쓰는 대표어. 최대 2개.
   * 검색량뿐 아니라 길이도 본다. title 은 35자 제한이라 긴 단어가 못 들어간다.
   * (스펙 §5 예시도 대표어 `골프조인동호회`(7자) 대신 `골프조인`(4자)을 썼다)
   * 비어 있으면 title 에 카테고리어를 넣지 않는다 - 검증 안 된 말을 지어내지 않는다. (스펙 §3 주석)
   */
  primary: string[]
  /** meta keywords 에만 붙이는 보조어 */
  extra: string[]
  /**
   * 지역명과 합성해 쓰는 어미. `{시도}독서모임` 형태.
   * 스펙 §1-3 은 합성을 금지하지만 검색량이 실측된 것만 예외로 둔다.
   */
  regionCompound?: string
  /** 소개글에 `triggers` 중 하나가 있을 때만 `keywords` 를 붙인다. (스펙 §1-4) */
  conditional?: { triggers: string[]; keywords: string[] }
}

/**
 * 공급자 언어(카테고리명)를 검색자 언어로 바꾸는 표. 괄호 안은 네이버 월 검색량.
 *
 * shared-utils 의 `INTEREST_LIST` 36개를 모두 키로 둔다.
 * 스펙에 매핑이 없는 카테고리는 빈 값으로 두어 채워야 할 칸이 눈에 보이게 한다.
 */
export const CATEGORY_KEYWORDS: Record<string, CategoryKeywords> = {
  // ── 스펙 §3 에 매핑이 있는 카테고리 ──
  골프: {
    primary: ['스크린골프', '골프조인'],
    extra: ['골프조인동호회', '골프조인어플', '파3골프장'],
  },
  소셜: {
    primary: ['정모', '번개모임'],
    extra: ['동네친구', '밥친구'],
    // 등산 모임에 `돌싱`을 붙이면 거짓 정보가 된다. 소개글에 그 말이 있을 때만 붙인다.
    conditional: { triggers: ['돌싱', '솔로', '싱글'], keywords: ['돌싱', '돌싱모임', '솔로'] },
  },
  여행: {
    primary: ['혼자여행', '여행동행'],
    extra: ['국내혼자여행', '당일치기여행', '여행메이트'],
  },
  등산: {
    primary: ['산악회', '트레킹'],
    extra: ['100대명산', '등산준비물'],
  },
  '걷기/러닝': {
    primary: ['둘레길', '러닝크루'],
    extra: ['트레킹', '산책'],
  },
  독서: {
    primary: ['독서모임'],
    extra: ['독서클럽'],
    regionCompound: '독서모임',
  },
  캠핑: {
    primary: ['백패킹', '차박'],
    extra: ['오토캠핑', '캠핑준비물'],
  },
  '사진/영상': {
    primary: ['출사'],
    extra: ['사진잘찍는법'],
  },
  파크골프: {
    primary: ['파크골프장'],
    extra: ['파크골프치는법', '파크골프규칙'],
  },
  배드민턴: {
    primary: ['배드민턴동호회'],
    extra: ['배드민턴클럽', '배드민턴레슨'],
    regionCompound: '배드민턴레슨',
  },
  탁구: {
    primary: ['탁구동호회'],
    extra: ['탁구레슨'],
    regionCompound: '탁구레슨',
  },
  자전거: {
    primary: ['자전거동호회'],
    extra: ['라이딩'],
  },
  '댄스/무용': {
    primary: ['라인댄스', '사교댄스'],
    extra: ['살사댄스', '라틴댄스', '지루박'],
  },
  '노래/보컬': {
    primary: ['노래교실'],
    extra: [],
  },

  // ── 스펙 §3 에 대표어(굵은 글씨) 지정은 없으나 검색량이 실측된 카테고리 ──
  // 실측값이 있어 대표어로 올렸다. (담당자 확인 완료)
  외국어: { primary: ['영어회화스터디'], extra: ['영어회화모임'] }, // 1,290 / 310
  레저: { primary: ['액티비티'], extra: [] }, // 7,860

  // 스펙은 `독서 계열로 흡수`라 했지만 자기계발 모임을 `독서모임`이라 부르면 실제 내용과
  // 어긋난다. title 에서는 빼고 원본 카테고리명(`공부 자기계발`)을 쓴다. (담당자 확인 완료)
  '공부/자기계발': { primary: [], extra: ['독서모임', '글쓰기모임', '스터디'] }, // 3,340 / 230

  // ── 검색량 미조사 카테고리 ──
  // 검증 안 된 말을 새로 만들지 않는다. (스펙 §3 주석) 활동어만 두고 대표어는 비운다.
  // 사전이 확장되면 primary 를 채운다.
  '악기/밴드': { primary: [], extra: ['악기배우기', '통기타', '밴드'] },
  '영화/공연': { primary: [], extra: ['전시회', '뮤지컬', '클래식공연', '미술관'] },
  '요리/베이킹': { primary: [], extra: ['쿠킹클래스', '베이킹'] },
  '그림/스케치': { primary: [], extra: ['드로잉', '어반스케치', '수채화'] },
  봉사활동: { primary: [], extra: ['봉사활동', '자원봉사'] },
  '당구/포켓볼': { primary: [], extra: ['당구동호회', '당구클럽'] },
  공예: { primary: [], extra: ['공방', '원데이클래스'] }, // 원데이클래스 21,980 - 경쟁이 심해 태그로만
  '차/오토바이': { primary: [], extra: ['드라이브코스', '라이딩'] }, // 4,600

  // ── 스펙 §3 에 매핑이 없는 카테고리 ──
  // 언어사전이 확장되면 이 칸을 채운다. 그때까지는 원본 카테고리명을 그대로 쓴다.
  족구: { primary: [], extra: [] },
  볼링: { primary: [], extra: [] },
  테니스: { primary: [], extra: [] },
  야구: { primary: [], extra: [] },
  '축구/풋살': { primary: [], extra: [] },
  '헬스/요가': { primary: [], extra: [] },
  승마: { primary: [], extra: [] },
  '바둑/장기/게임': { primary: [], extra: [] },
  반려동물: { primary: [], extra: [] },
  낚시: { primary: [], extra: [] },
  야구관람: { primary: [], extra: [] },
}

/**
 * 원본 카테고리명을 검색 가능한 단어로 나눈다.
 *
 * `골프`·`독서`는 그 자체가 검색어지만 `악기/밴드`·`공부/자기계발` 처럼 슬래시가 든 표기는
 * 아무도 검색창에 입력하지 않는다. 슬래시로 나누면 `악기` `밴드` 각각이 단어로 읽힌다.
 * title·h1 처럼 사람이 읽는 자리에서도 슬래시보다 자연스럽다.
 *
 * @example
 * splitInterestLabel('악기/밴드')      // ['악기', '밴드']
 * splitInterestLabel('바둑/장기/게임')  // ['바둑', '장기', '게임']
 * splitInterestLabel('독서')           // ['독서']
 */
export const splitInterestLabel = (interest: string | null | undefined): string[] =>
  interest ? uniq(interest.split('/')) : []

export type CategoryResult = {
  /** title 에 넣을 대표어. 비어 있으면 title 에 카테고리어를 넣지 않는다 */
  primary: string[]
  /** meta keywords 에 넣을 전체 키워드 */
  keywords: string[]
}

/**
 * 카테고리명을 검색 키워드로 바꾼다. (스펙 §3)
 *
 * @example
 * categoryKeywords('골프')
 * // { primary: ['스크린골프', '골프조인'],
 * //   keywords: ['스크린골프', '골프조인', '골프조인동호회', '골프조인어플', '파3골프장', '골프'] }
 *
 * categoryKeywords('노래/보컬')
 * // { primary: ['노래교실'], keywords: ['노래교실', '노래', '보컬'] }  ← 슬래시로 나눠 넣는다
 *
 * categoryKeywords('볼링')
 * // { primary: [], keywords: ['볼링'] }                ← 매핑이 없으면 원본을 그대로 쓴다
 *
 * @param regionKeywords `regionCompound` 가 있는 카테고리에서 `{시도}독서모임` 을 만들 때 쓴다.
 *   {@link splitRegion} 의 결과를 그대로 넘기면 된다.
 * @param description 소개글. `conditional` 이 있는 카테고리에서 조건 판정에 쓴다.
 *   모임 소개글에 해당 단어가 있을 때만 태그를 붙인다. (스펙 §1-4)
 */
export const categoryKeywords = (
  interest: string | null | undefined,
  options: { regionKeywords?: string[]; description?: string } = {}
): CategoryResult => {
  if (!interest) return { primary: [], keywords: [] }

  const mapped = CATEGORY_KEYWORDS[interest]

  // 매핑이 없거나 비어 있으면 원본 카테고리명을 쓴다.
  if (!mapped || (mapped.primary.length === 0 && mapped.extra.length === 0)) {
    return { primary: [], keywords: splitInterestLabel(interest) }
  }

  const keywords = [...mapped.primary, ...mapped.extra]

  // 검색량이 실측된 지역 합성어만 만든다. 시도(첫 번째 키워드) 하고만 합성한다.
  const [majorRegion] = options.regionKeywords ?? []
  if (mapped.regionCompound && majorRegion) {
    keywords.push(`${majorRegion}${mapped.regionCompound}`)
  }

  // 소개글에 근거가 있을 때만 붙이는 조건부 태그
  if (mapped.conditional && options.description) {
    const hit = mapped.conditional.triggers.some((word) => options.description!.includes(word))
    if (hit) keywords.push(...mapped.conditional.keywords)
  }

  keywords.push(...splitInterestLabel(interest))

  return { primary: mapped.primary, keywords: uniq(keywords) }
}

// ────────────────────────────────────────────────────────────────────────────
// 공통 태그 (스펙 §2-1)
// ────────────────────────────────────────────────────────────────────────────

/**
 * 모든 모임에 공통으로 붙이는 키워드.
 *
 * 기존에 붙던 `중장년층`(0) · `취미활동` · `문화활동` · `여가활동`(~0) ·
 * `채팅` · `친구` · `모임` · `취미` 는 검색량이 없어 전부 걷어냈다.
 * `소모임어플`·`모임어플` 은 앱 설치를 찾는 검색어라 우리 전환 목표와도 맞는다.
 */
export const COMMON_SEO_KEYWORDS = [
  '동호회', // 3,660
  '소모임어플', // 1,810
  '모임어플', // 1,160
  '오이',
]

// ────────────────────────────────────────────────────────────────────────────
// title (스펙 §2-3)
// ────────────────────────────────────────────────────────────────────────────

/**
 * 대표어를 하나로 줄이는 길이 기준.
 *
 * 사이트맵 136건 기준 모임명 길이는 중앙값 11자 / 최대 25자다.
 * 대표어 2개를 붙이면 26건(19%)이 이 기준을 넘고, 1개로 줄이면 5건(4%)만 넘는다.
 * 모임명은 고유명사라 자르지 않는다. 넘치면 대표어를 줄인다.
 */
const TITLE_SOFT_LIMIT = 40

/**
 * 모임 상세의 `<title>` 을 만든다. (스펙 §2-3)
 *
 * `{모임명} — {지역} {대표어 1~2개} | 오이`
 *
 * title 은 meta keywords 보다 검색 가중치가 훨씬 높아서, 씬 고유어가 여기 들어가는 것이
 * 이 스펙에서 가장 효과 큰 단일 변경이다. (스펙 §2-3)
 *
 * @example
 * buildGatheringTitle({ name: '♤ 명랑골프동호회 ♤', region: '광주', primary: ['스크린골프', '골프조인'] })
 * // '♤ 명랑골프동호회 ♤ — 광주 스크린골프 골프조인 | 오이'
 *
 * buildGatheringTitle({ name: '뜨개 쉼표', region: '서울', primary: [] })
 * // '뜨개 쉼표 — 서울 | 오이'   ← 대표어가 없는 카테고리
 */
export const buildGatheringTitle = (gathering: {
  name: string
  /** 시도 축약명. {@link splitRegionParts} 의 `major` */
  region: string
  /** {@link categoryKeywords} 의 `primary` */
  primary: string[]
}): string => {
  const compose = (primary: string[]) => {
    const middle = [gathering.region, ...primary].filter(Boolean).join(' ')
    return [gathering.name.trim(), middle].filter(Boolean).join(' — ') + ' | 오이'
  }

  const full = compose(gathering.primary)
  if (full.length <= TITLE_SOFT_LIMIT || gathering.primary.length <= 1) return full

  return compose(gathering.primary.slice(0, 1))
}


// ────────────────────────────────────────────────────────────────────────────
// 모임일정 장소 (스펙 §4-1)
// ────────────────────────────────────────────────────────────────────────────

/**
 * 장소가 정해지지 않았을 때 들어오는 값. 사용자 입력이라 표기가 제각각이다.
 */
const PLACE_PLACEHOLDERS = [
  '추후결정',
  '추후공지',
  '추후정해요',
  '미정',
  '장소미정',
  '미확정',
  '협의',
  '협의후결정',
  '당일공지',
  '단톡방공지',
  '공지예정',
]

/** 전화번호·URL 이 장소 필드에 섞여 들어오는 경우 (스펙 §4-1 안전장치) */
const PLACE_CONTACT_PATTERN = /(\d{2,4}-\d{3,4}-\d{4})|(01\d{9,10})|(https?:\/\/)|(www\.)|(\.com)|(\.kr)/i

/**
 * 지하철 노선·출구 표기. `홍대입구역 2호선` 은 검색어가 아니지만 `홍대입구역` 은 검색어다.
 *
 * 노선명은 명시한 것만 지운다. `[가-힣]+선` 같은 일반 규칙을 쓰면 `경의선숲길` 처럼
 * 그 자체가 검색어인 지명까지 깨진다.
 */
const TRANSIT_EXIT_PATTERN = /\d+\s*번\s*출구/g
const TRANSIT_LINE_TOKEN =
  /^(\d+호선|경의중앙선|신림선|신분당선|수인분당선|우이신설선|서해선|경춘선|공항철도|인천\d호선)$/

/** 20자를 넘는 장소는 버린다. 잘라내면 `북한산국립공원 도봉탐방지원센...` 같은 깨진 단어가 남는다 */
const PLACE_MAX_LENGTH = 20

/** 모임당 붙이는 장소 개수 상한 (스펙 §4-1 은 상위 3~5개) */
const PLACE_LIMIT = 5

const cleanPlace = (place: string): string =>
  place
    .replace(TRANSIT_EXIT_PATTERN, ' ')
    .split(/\s+/)
    .filter((token) => token && !TRANSIT_LINE_TOKEN.test(token))
    .join(' ')
    .trim()

/**
 * 모임일정의 장소를 검색 키워드로 바꾼다. (스펙 §4-1)
 *
 * 고유명사가 카테고리어를 압도한다는 것이 스펙에서 4회 반복 확인된 규칙이다.
 * (산악회 2,980 vs 청계산 17,590 / 등산코스추천 80 vs 북한산등산코스 12,260)
 * 장소는 모임마다 전부 달라서, 페이지 하나하나가 서로 다른 검색어 접점을 갖게 된다.
 *
 * 짧은 상호명(`덕자`, `단향`)도 버리지 않는다. 같은 페이지에 지역 키워드가 따로 들어가므로
 * `광주 덕자` 같은 조합 검색에 걸린다. 합성하지 않고 병렬로 두는 원칙과 같다. (스펙 §1-3)
 *
 * 스펙의 `빈도 1회 제외` 는 적용하지 않는다. 현재 API 가 향후 일정만 주기 때문에
 * (`meet_time > now`) 모임당 일정이 1~4건뿐이고, 빈도로 거르면 대부분이 사라진다.
 *
 * @example
 * normalizePlaces(['홍대입구역 2호선', '추후 결정', '롯데월드타워', '롯데월드타워'])
 * // ['홍대입구역', '롯데월드타워']
 */
export const normalizePlaces = (places: (string | null | undefined)[]): string[] => {
  const cleaned = places
    .map((place) => cleanPlace((place ?? '').trim()))
    .filter((place) => place.length > 0 && place.length <= PLACE_MAX_LENGTH)
    .filter((place) => !PLACE_CONTACT_PATTERN.test(place))
    .filter((place) => !PLACE_PLACEHOLDERS.includes(place.replace(/\s+/g, '')))

  return uniq(cleaned).slice(0, PLACE_LIMIT)
}

// ────────────────────────────────────────────────────────────────────────────
// 본문 사전 매칭 (스펙 §4-2)
// ────────────────────────────────────────────────────────────────────────────

/**
 * 매칭 사전. 언어사전 v2.2 2부 `쓸 것` 컬럼 + 산·관광지명. (스펙 §4-2 로직 1)
 *
 * 자유 텍스트에서 단어를 "추출"하지 않고, 이 목록에 있는 단어가 본문에 있는지
 * "매칭"만 한다. 사전에 없는 닉네임·회비·계좌번호는 구조적으로 나갈 수 없다. (스펙 §1-2)
 *
 * 발행 후 서치어드바이저에서 잡힌 유입어를 여기에 역으로 추가해 나간다.
 * (언어사전 §8-5 — 추정을 실측으로 대체하는 유일한 경로)
 */
const CONTENT_DICTIONARY = [
  // 골프
  '골프조인동호회', '골프조인어플', '골프조인', '조인골프', '골프조인앱',
  '스크린골프', '골프연습장', '파3골프장',
  '골프여행', '골프투어', '골프패키지', '국내골프여행', '국내골프투어', '국내골프패키지',
  '1박2일골프패키지', '국내1박2일골프패키지', '2인골프', '2인플레이골프장',
  '노캐디골프장', '골프2인플레이', '노캐디',
  // 등산·걷기
  '트레킹', '둘레길', '러닝크루', '산악회', '산행', '등산준비물', '100대명산', '코리아둘레길',
  // 산·코스 이름 (스펙 §4-2 의 `(선택) 전국 산·관광지명 리스트`)
  // 고유명사가 카테고리어를 압도한다. (산악회 2,980 vs 청계산 17,590)
  '북한산', '청계산', '광교산', '금수산', '모악산', '천태산', '가리산', '명지산',
  '두타산', '가리왕산', '함백산', '선자령', '운탄고도', '마니산', '백아산',
  '한라산', '설악산', '지리산',
  // 여행
  '혼자여행', '국내혼자여행', '여자혼자국내여행', '여자혼자여행',
  '여행동행', '여행동행카페', '여행메이트', '뚜벅이여행',
  '당일치기여행', '국내당일치기여행', '1박2일여행지', '국내1박2일여행지', '서울근교여행',
  // 소셜
  '돌싱', '돌싱모임', '돌싱카페', '돌싱소개팅', '돌싱연애', '돌싱만남',
  '돌싱재혼', '돌싱남', '돌싱녀', '재혼', '재혼사이트',
  '정모', '번개모임', '동네친구', '맛집투어', '밥친구',
  // 독서·글쓰기
  '독서모임', '독서클럽', '온라인독서모임', '글쓰기모임', '소설쓰는법', '공부하기좋은카페',
  // 파크골프
  '파크골프치는법', '파크골프규칙', '파크골프장', '파크골프레슨', '파크골프동호회',
  // 다이닝
  '소셜다이닝', '대화모임',
  // 캠핑
  '백패킹', '백패킹배낭', '차박', '차박장소추천', '오토캠핑', '캠핑준비물',
  // 사진
  '출사', '사진잘찍는법',
  // 음악·댄스
  '노래교실', '라인댄스', '사교댄스', '지루박', '살사댄스', '라틴댄스',
  // 구기·생활체육
  '배드민턴레슨', '배드민턴동호회', '배드민턴클럽', '탁구레슨', '탁구동호회', '자전거동호회',
]

/**
 * 본문에 있어도 절대 내보내지 않는 말. (언어사전 4부 `함정 목록`)
 *
 * 볼륨은 크지만 검색자가 원하는 것이 다르다. 장비를 사려는 사람, 관람하려는 사람,
 * 일자리를 찾는 사람은 우리 방문자가 아니다.
 * 실측: 운영 모임 본문에 전시회 10건 · 캠핑용품 2건 · 등산화 1건 등이 실제로 있었다.
 */
const CONTENT_TRAP_WORDS = [
  '등산화', '트레킹화', '캠핑용품', '캠핑의자', '파크골프채', // 장비 구매
  '원데이클래스', '댄스학원', '영어학원', '영어공부혼자하기', // 학원·유료 클래스
  '시니어일자리', '노인일자리', // 구직
  '결정사', '결혼정보회사', '소개팅어플', // 결혼정보회사 시장
  '크루즈여행', '워터파크', '전시회', '파티룸', '서울파티룸', // 상품·관람·공간 대여
  '안내산악회', // 유료 상업 산악회
  '나는솔로', // TV 프로그램
  '자기계발', // 공모전·양식 다운로드 수요
]

const MATCHED_WORD_LIMIT = 10

/**
 * 모임 본문에서 사전에 있는 단어를 찾아 키워드로 승격한다. (스펙 §4-2)
 *
 * §3 카테고리 매핑은 `이 모임은 사진 카테고리다 → 사진 씬 단어를 붙인다` 이므로
 * 카테고리 경계를 넘는 활동을 잡지 못한다. 사진 모임이 `정모` 도 하고 `트레킹` 도 가는데
 * 그 말이 본문에 이미 적혀 있어도 메타에는 반영되지 않았다. 이 함수가 그 빈틈을 메운다.
 *
 * @param texts 모임 소개글 · 최근 공지 · 최근 피드 20건. 전부 SSR 페이로드에 이미 들어있다.
 * @param exclude 이미 다른 규칙(카테고리 · 지역 · 장소)이 넣은 키워드. 중복을 피한다.
 */
export const matchContentKeywords = (
  texts: (string | null | undefined)[],
  exclude: string[] = []
): string[] => {
  const haystack = texts.filter(Boolean).join(' ')
  if (!haystack) return []

  const excluded = new Set(exclude)
  const matched = CONTENT_DICTIONARY.filter(
    (word) =>
      !excluded.has(word) && !CONTENT_TRAP_WORDS.includes(word) && haystack.includes(word)
  )

  return uniq(matched).slice(0, MATCHED_WORD_LIMIT)
}

// ────────────────────────────────────────────────────────────────────────────
// 피드 title (스펙 §4-3)
// ────────────────────────────────────────────────────────────────────────────

/** 이모지·픽토그램. `영화 ⚓️ 오디세이 🏛` → `영화 오디세이` */
const EMOJI_PATTERN = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}]/gu
/** 강조용으로 반복해 쓴 특수문자. `****피맥벙***` → `피맥벙` */
const DECORATION_PATTERN = /[*~"'=_\-.]{2,}/g
/** 앞뒤에 남은 장식·공백 */
const EDGE_NOISE_PATTERN = /^[\s*~"'=_\-.]+|[\s*~"'=_\-.]+$/g

/**
 * 사용자가 쓴 텍스트를 `<title>` 에 넣을 수 있게 정리한다.
 *
 * 개행을 그대로 두면 title 안에 줄바꿈이 들어가고, 이모지·장식 문자는 검색결과에서
 * 자리만 차지한다.
 */
export const sanitizeTitleText = (text: string | null | undefined): string =>
  (text ?? '')
    .replace(EMOJI_PATTERN, ' ')
    .replace(DECORATION_PATTERN, ' ')
    .replace(EDGE_NOISE_PATTERN, '')
    .replace(/\s+/g, ' ')
    .trim()

/** 정제 후 이 길이 미만이면 제목 구실을 못 한다고 보고 본문으로 넘어간다 */
const MIN_HEADLINE_LENGTH = 3
/** 앞부분(일정 제목 또는 본문)을 자르는 길이 */
const FEED_HEADLINE_LENGTH = 30
/**
 * 모임명까지 붙였을 때의 상한. 넘으면 모임명을 뺀다.
 *
 * title 은 길다고 감점되지 않고 표시될 때 잘릴 뿐이라 절대 기준은 없다.
 * 58 은 실측으로 잡은 값이다 - 일정 제목이 있는 피드(39~57자)가 전부 모임명을
 * 유지하는 최소값이다. 더 올리면 본문을 쓰는 피드까지 들어오면서 title 이 70자를 넘는다.
 */
const FEED_TITLE_SOFT_LIMIT = 58

const truncate = (text: string, maxLength: number) =>
  text.length > maxLength ? text.slice(0, maxLength) + '...' : text

/**
 * 피드 상세의 `<title>` 을 만든다. (스펙 §4-3)
 *
 * `{일정 제목 또는 본문} — {지역} {모임명} | 오이`
 *
 * 일정 제목을 앞세우는 이유는, 그것이 본문보다 검색어 밀도가 높기 때문이다.
 * 모임장이 "무엇을 하는 모임인지" 요약해 쓴 문장이라 지명·활동명이 앞에 온다.
 * 실측: `궁평항 정모 첫날점심` / `우이령 숲길을 걸어서` / `[뮤지컬] 드라큘라`.
 * 이 지명들은 우리 매칭 사전에 없어서 §4-2 로는 잡히지 않는다.
 *
 * @example
 * buildFeedTitle({ scheduleTitle: '궁평항 정모 첫날점심', body: '또한번의 설레는 맘으로...',
 *                  region: ['서울','관악'], gatheringName: '핫플레이스 탐방 여행모임' })
 * // '궁평항 정모 첫날점심 — 서울 관악 핫플레이스 탐방 여행모임 | 오이'
 */
export const buildFeedTitle = (feed: {
  scheduleTitle?: string | null
  body?: string | null
  gatheringName: string
  /** {@link splitRegion} 의 결과 */
  region: string[]
}): string => {
  const schedule = sanitizeTitleText(feed.scheduleTitle)
  const body = sanitizeTitleText(feed.body)
  const source = schedule.length >= MIN_HEADLINE_LENGTH ? schedule : body
  const headline = truncate(source, FEED_HEADLINE_LENGTH) || feed.gatheringName

  const compose = (name: string) =>
    [headline, [feed.region.join(' '), name].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(' — ') + ' | 오이'

  // 잘린 모임명(`핫플레이스 탐방 여행...`)은 넣지 않느니만 못하다. 넘치면 통째로 뺀다.
  const full = compose(feed.gatheringName)
  return full.length <= FEED_TITLE_SOFT_LIMIT ? full : compose('')
}

// ────────────────────────────────────────────────────────────────────────────
// meta description (스펙 §7-2)
// ────────────────────────────────────────────────────────────────────────────

/**
 * 검색 스니펫에 나가면 안 되는 것들.
 *
 * 소개글은 사용자가 자유롭게 쓰는 글이라 연락처·계좌가 섞여 들어온다.
 * meta description 은 검색결과에 그대로 노출되므로 색인되면 검색으로 찾을 수 있게 된다.
 * 실측(운영 모임 138건): 전화번호 6건 · 계좌번호 1건. 그중 2건은 앞 80자 안에 있었다.
 */
const CONTACT_PATTERNS: RegExp[] = [
  /01[016789][-\s.]?\d{3,4}[-\s.]?\d{4}/g, // 휴대폰
  /0\d{1,2}[-\s.]\d{3,4}[-\s.]\d{4}/g, // 일반전화
  /\d{2,6}[-\s]\d{2,6}[-\s]\d{2,7}(?:[-\s]\d{1,3})?/g, // 계좌번호
  /(?:https?:\/\/|www\.)\S+/g, // URL
]

/** 검색결과에 보이는 길이. 그 뒤는 잘리므로 더 담아도 스니펫 품질에 도움이 안 된다 */
const DESCRIPTION_LENGTH = 80

/**
 * 소개글을 검색 스니펫용으로 정제한다. (스펙 §7-2)
 *
 * 스펙은 `소개글 첫 80자 + 이모지·특수문자 제거` 를 요청했다.
 * 여기에 연락처 제거를 더했다 - 실측에서 전화번호·계좌번호가 실제로 노출되고 있었다.
 *
 * @example
 * buildDescription('🎀 프란체스카(리더) : 010-9186-5915 📞 ✨️서울ㆍ경기 트레킹 모임입니다')
 * // '프란체스카(리더) : 서울ㆍ경기 트레킹 모임입니다'
 */
export const buildDescription = (text: string | null | undefined): string => {
  const stripped = CONTACT_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, ' '),
    text ?? ''
  )
  return truncate(sanitizeTitleText(stripped), DESCRIPTION_LENGTH)
}
